<script lang="ts" setup>
import GICS from 'gics'
import type { CompanyItem } from '~/models/Company'

interface Props {
  company?: CompanyItem
  jobs?: []
}

const props = defineProps<Props>()
const { $config } = useNuxtApp()
const route = useRoute()
const router = useRouter()
const activeImg = ref(0)
const companyDetail = ref(props.company)
const gicsStructure = ref(companyDetail.value?.gicsIndustry ? new GICS(companyDetail.value?.gicsIndustry.subIndustry) : [])
const jobOpenings = ref(props.jobs)

if (!isEmpty(companyDetail.value)) {
  // seo
  const metaTitle = computed(() => `${companyDetail.value?.name} Careers and Profile Overview | Epicareer`)
  const metaDesc = computed(() => `Want to work at ${companyDetail.value?.name}? Learn what its like to work here. See job vacancies, salaries, benefits, location, photos, videos, and more.`)
  const robots = ($config.public.ENV === 'development' || $config.public.ENV === 'staging') ? { name: 'robots', content: 'nofollow, noindex' } : { name: 'robots', content: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1' }

  useHead({
    title: metaTitle.value,
    meta: [
      { property: 'og:title', content: metaTitle.value },
      { name: 'twitter:title', content: metaTitle.value },
      { name: 'description', content: metaDesc.value },
      { property: 'og:description', content: metaDesc.value },
      { name: 'twitter:description', content: metaDesc.value },
      { property: 'og:url', content: useSetSubdomain(useGetSubdomain()) + route.path },
      robots,
    ],
    link: [
      { rel: 'canonical', href: useSetSubdomain(useGetSubdomain()) + route.path },
    ],
  })
}
</script>

<template>
  <div v-if="!isEmpty(companyDetail)" class="mt-6 space-y-6">
    <!-- Company Detail -->
    <div class="grid md:grid-cols-2 gap-6">
      <div class="space-y-6 order-1 md:order-none">
        <!-- ABOUT -->
        <div class="bg-white p-4 md:p-6 rounded-md shadow-md">
          <h1 class="text-2xl md:text-3xl font-bold mb-2">
            About {{ companyDetail?.name }}
          </h1>
          <div class="text-gray-600 text-sm" v-html="companyDetail?.description" />
        </div>

        <!-- GICS -->
        <div class="bg-white p-4 md:p-6 rounded-md shadow-md">
          <h2 class="text-2xl md:text-3xl font-bold mb-2">
            Industry Type
          </h2>
          <div class="text-gray-600 text-sm">
            According to the GICS (Global Industry Classification Standard) version of March 2023,
            {{ companyDetail?.name }}’s industry classification falls within the following categories:

            <ul class="mt-4 list-disc ml-4">
              <li>Sector: {{ !isEmpty(gicsStructure) && gicsStructure.level(1) ? gicsStructure.level(1).name : '-' }}</li>
              <li>Industry group: {{ !isEmpty(gicsStructure) && gicsStructure.level(2) ? gicsStructure.level(2).name : '-' }}</li>
              <li>Industry: {{ !isEmpty(gicsStructure) && gicsStructure.level(3) ? gicsStructure.level(3).name : '-' }}</li>
              <li>Sub-Industry: {{ !isEmpty(gicsStructure) && gicsStructure.level(4) ? gicsStructure.level(4).name : '-' }}</li>
            </ul>
          </div>
        </div>
      </div>

      <!-- MEDIA -->
      <div v-if="!isEmpty(companyDetail?.previewMedia)" class="bg-white p-6 pb-10 rounded-md shadow-md" style="height: fit-content;">
        <div class="text-2xl font-bold mb-6">
          Videos and Photos
        </div>
        <div class="h-46 md:h-80 mb-4 md:mb-6 flex">
          <img v-if="companyDetail?.previewMedia[activeImg]?.type === 'photo'" :src="companyDetail?.previewMedia[activeImg]?.url" class="rounded-md m-auto max-h-46 md:max-h-80">
          <iframe v-else :src="companyDetail?.previewMedia[activeImg]?.url" class="rounded-md m-auto w-full h-full max-h-46 md:max-h-80" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen />
        </div>
        <BaseAtomsButton size="sm" :outline="true" class="px-8 py-2 font-medium focus:ring-0 focus:ring-offset-0 w-full font-smmibold" @click="router.push(`${route.path}/videos-photos`)">
          See All
        </BaseAtomsButton>
      </div>
      <div v-else class="bg-gray-100 py-8 h-min grid">
        <img class="w-62 mx-auto mb-7" src="/images/company/no-img.png">
        <p class="text-xl font-bold mx-auto">
          No preview available
        </p>
      </div>
    </div>

    <!-- Company Map -->
    <div v-if="companyDetail?.location?.lat && companyDetail?.location?.lon" class="bg-white p-4 md:p-6 space-y-6 rounded-md shadow-md">
      <h2 class="text-2xl md:text-3xl font-bold">
        Company Address
      </h2>
      <div v-if="companyDetail?.location?.address" class="flex items-start mb-6">
        <Icon name="ic:sharp-location-on" class="w-6 h-6 mr-2 text-[#E0124E] flex-shrink-0" />
        {{ companyDetail?.location?.address }}
      </div>
      <iframe class="mb-6" :src="`https://www.google.com/maps?q=${`${companyDetail?.location?.lat},${companyDetail?.location?.lon}`}&z=16&output=embed`" height="500" width="100%" style="border: 0" allowfullscreen loading="lazy" referrerpolicy="no-referrer-when-downgrade" />
      <p class="text-xs text-gray-600">
        Source : Google maps
      </p>
    </div>

    <!-- Latest Job Opening -->
    <div v-if="!isEmpty(jobOpenings)" class="<md:px-4">
      <h2 class="text-2xl md:text-3xl font-bold mb-4 lg:mb-6">
        Latest Job Openings
      </h2>
      <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-4 lg:gap-6">
        <BrowsejobsJobCard v-for="(jobItem, idx) in jobOpenings" :key="idx" :show-dots="false" impression-source="company-page" :item="jobItem" :job-id="jobItem.job_id" />
      </div>
    </div>

    <!-- Similar Company -->
    <div v-if="!isEmpty(companyDetail?.similarCompanies)" class="<md:px-4">
      <h2 class="text-2xl md:text-3xl font-bold mb-4 lg:mb-6">
        Companies in Similar Industry
      </h2>
      <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-4 lg:gap-6">
        <CompaniesCompanyCardV1 v-for="(companyItem, idx) in companyDetail?.similarCompanies" :key="idx" impression-source="company-page" :lite="true" :item="companyItem" />
      </div>
    </div>
  </div>
</template>
